import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { setUser } from '@sentry/angular-ivy';
import { TuiAlertService, TuiDialogService } from '@taiga-ui/core';
import { ENVIRONMENT } from '@us/app-config';
import { selectApiDomain } from './auth.reducer';
import { AppInit } from '@us/web/shared/app';
import { ApiService } from '@us/web/shared/data-access';
import { catchError, EMPTY, map, of, switchMap, tap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthActions } from './auth.actions';
import { pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { getErrorMessage } from '@us/web/shared/utils';
import { TUI_PROMPT } from '@taiga-ui/kit';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const FB: any;

@Injectable()
export class AuthEffects {
    private readonly actions$ = inject(Actions);
    private readonly authService = inject(AuthService);
    private readonly alertService = inject(TuiAlertService);
    private readonly dialogService = inject(TuiDialogService);
    private readonly translocoService = inject(TranslocoService);
    private readonly apiService = inject(ApiService);
    private readonly store = inject(Store);
    private readonly env = inject(ENVIRONMENT);

    getToken$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.getToken),
            switchMap(() =>
                this.authService.getAccessToken().pipe(
                    map(response => {
                        return AuthActions.getTokenSuccess({ data: response });
                    }),
                    catchError(() => EMPTY)
                )
            )
        );
    });

    logout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.logout),
            switchMap(() =>
                this.dialogService
                    .open<boolean>(TUI_PROMPT, {
                        label: this.translocoService.translate('are_you_sure_you_want_to_sign_out'),
                        size: 's'
                    })
                    .pipe(
                        map(response => {
                            if (response) {
                                return AuthActions.logoutConfirmed();
                            }
                            return AuthActions.logoutNotConfirmed();
                        })
                    )
            )
        );
    });

    logoutConfirmed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.logoutConfirmed),
            tap(() => {
                try {
                    FB.logout();
                } catch (e) {
                    console.error(e);
                }
            }),
            switchMap(() =>
                this.authService.getAccessToken(true).pipe(
                    map(response => {
                        return AuthActions.getTokenSuccess({ data: response });
                    }),
                    catchError(() => EMPTY)
                )
            ),
            tap(() => {
                this.alertService
                    .open(this.translocoService.translate('logout_success'), { status: 'success' })
                    .subscribe();
            })
        );
    });

    login$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.login),
            switchMap(({ params }) => {
                return this.authService.login(params).pipe(
                    map(response => {
                        this.alertService
                            .open(this.translocoService.translate('logged_in_successfully') as string, {
                                status: 'success'
                            })
                            .subscribe();
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        const { access_token, token_type, expires_in, api_domain, ...user } = response;

                        if (this.env.production) {
                            setUser({
                                id: user.id.toString(),
                                username: user.username || user.phone,
                                email: user.email || undefined
                            });
                        }

                        return AuthActions.loginSuccess({
                            user,
                            token: { access_token, token_type, expires_in, api_domain }
                        });
                    }),
                    catchError(error => {
                        const message =
                            error.error?.error_description?.toString() ||
                            this.translocoService.translate('message.error');

                        this.alertService.open(message, { status: 'error' }).subscribe();
                        return of(AuthActions.loginFailure({ error: message }));
                    })
                );
            })
        );
    });

    getTokenSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AuthActions.getTokenSuccess),
                tap(({ data }) => {
                    this.apiService.setApiDomain(data.api_domain);
                })
            );
        },
        { dispatch: false }
    );

    updateAccountProfile$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.updateAccountProfile),
            pessimisticUpdate({
                run: ({ params, onSuccess }) => {
                    return this.authService.updateProfile(params).pipe(
                        map(response => {
                            onSuccess?.();
                            this.alertService
                                .open(this.translocoService.translate('success'), { status: 'success' })
                                .subscribe();
                            return AuthActions.updateAccountProfileSuccess(response);
                        })
                    );
                },
                onError: (_, error) => {
                    const message = getErrorMessage(error);
                    this.alertService.open(message, { status: 'error' }).subscribe();
                    return of(AuthActions.updateAccountProfileFailure({ error: message }));
                }
            })
        );
    });

    init$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(AppInit),
                concatLatestFrom(() => this.store.select(selectApiDomain)),
                tap(([, apiDomain]) => {
                    if (apiDomain) {
                        this.apiService.setApiDomain(apiDomain);
                    }
                })
            );
        },
        { dispatch: false }
    );
}
